import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { retrieveLaunchParams, retrieveRawInitData } from '@tma.js/sdk'
import { authApi } from '../api/authApi'
import { userApi } from '../api/userApi'
import type { User } from '../types/api'
import { Sentry } from '../sentry'
import { captureEvent, resetPostHogUser, syncAnalyticsUser } from '../posthog'

function getTelegramStartParam(): string | undefined {
  try {
    const { tgWebAppStartParam } = retrieveLaunchParams()
    return tgWebAppStartParam || undefined
  } catch {
    return undefined
  }
}

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    authenticateUser()
  }, [])

  async function authenticateUser() {
    try {
      setIsLoading(true)
      setError(null)

      // Get Telegram initData
      const rawInitiData = retrieveRawInitData()
      if (!rawInitiData) {
        throw new Error('No Telegram initData found')
      }

      console.log('Authenticating with Telegram initData...')

      // Send initData to backend for authentication
      const response = await authApi.authenticate(rawInitiData)

      // Store token
      setToken(response.token)
      localStorage.setItem('auth_token', response.token)

      console.log('Authentication successful, fetching user info...')

      // Fetch current user info with the token
      const userData = await userApi.getCurrentUser()
      setUser(userData)
      Sentry.setUser({ id: String(userData.id) })

      const startParam = getTelegramStartParam()
      syncAnalyticsUser(
        userData,
        startParam ? { start_param: startParam } : undefined,
      )
      captureEvent('app_opened', startParam ? { start_param: startParam } : undefined)

      console.log('User info loaded:', userData)
    } catch (err) {
      console.error('Authentication failed:', err)
      setError(err instanceof Error ? err.message : 'Authentication failed')
      
      // Clear any stored token
      localStorage.removeItem('auth_token')
      Sentry.setUser(null)
      resetPostHogUser()
    } finally {
      setIsLoading(false)
    }
  }

  function logout() {
    setToken(null)
    setUser(null)
    localStorage.removeItem('auth_token')
    Sentry.setUser(null)
    resetPostHogUser()
  }

  async function refreshUser() {
    if (!token) return
    try {
      const userData = await userApi.getCurrentUser()
      setUser(userData)
      Sentry.setUser({ id: String(userData.id) })
      syncAnalyticsUser(userData)
    } catch (err) {
      console.error('Failed to refresh user:', err)
    }
  }

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!token && !!user,
    error,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Separate file recommended for hooks to avoid fast-refresh issues
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
